import { useCallback, useEffect, useRef, useState } from "react";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { readErrorMessage } from "./riskErrors";

export type DraftSaveStatus = 'idle' | 'saved' | 'saving' | 'error' | 'conflict';

export const DRAFT_AUTOSAVE_INTERVAL = 60000;

export interface DraftAutosave {
	dirty: boolean;
	status: DraftSaveStatus;
	savedAt: string | null;
	message: string;
	start: (version: number, state: string) => void;
	save: () => Promise<boolean>;
}

export function useDraftAutosave(draftId: string | undefined, snapshot: string, interval = DRAFT_AUTOSAVE_INTERVAL): DraftAutosave {
	const [baseline, setBaseline] = useState<string | null>(null);
	const [status, setStatus] = useState<DraftSaveStatus>('idle');
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [message, setMessage] = useState('');
	const version = useRef(0);
	const latest = useRef(snapshot);
	const saved = useRef<string | null>(null);
	const pending = useRef<Promise<boolean> | null>(null);
	const blocked = useRef(false);
	latest.current = snapshot;

	const start = useCallback((nextVersion: number, state: string) => {
		version.current = nextVersion;
		saved.current = state;
		blocked.current = false;
		setBaseline(state);
		setStatus('saved');
		setSavedAt(null);
		setMessage('');
	}, []);

	const save = useCallback(() => {
		if (!draftId || blocked.current || saved.current === null || latest.current === saved.current) {
			return Promise.resolve(!blocked.current);
		}
		if (pending.current) {
			return pending.current;
		}
		const state = latest.current;
		setStatus('saving');
		pending.current = new RiskAssessmentService().saveDraft(draftId, version.current, state)
			.then(response => {
				version.current = response.data.version;
				saved.current = state;
				setBaseline(state);
				setSavedAt(response.data.savedAt);
				setStatus('saved');
				return true;
			})
			.catch(async error => {
				const text = await readErrorMessage(error, 'Não foi possível salvar o rascunho. Uma nova tentativa será feita em instantes.');
				if (error?.response?.status === 409) {
					blocked.current = true;
					setStatus('conflict');
				} else {
					setStatus('error');
				}
				setMessage(text);
				return false;
			})
			.finally(() => {
				pending.current = null;
			});
		return pending.current;
	}, [draftId]);

	useEffect(() => {
		if (!draftId) {
			return;
		}
		const timer = setInterval(() => {
			save();
		}, interval);
		return () => clearInterval(timer);
	}, [draftId, save, interval]);

	return { dirty: baseline !== null && snapshot !== baseline, status, savedAt, message, start, save };
}